/**
 * Spec 089b — Search Console property picker.
 *
 * Mounted by SearchConsoleIntegrationsSection when the URL has
 * `?gsc_connect=success` after Google's OAuth callback. Fetches the verified
 * properties for the authorized user, lets them pick one, and binds it via
 * POST .../search-console/select-property.
 */

import { useEffect, useState } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import { apiFetch } from '../lib/api.js';
import { apiPost } from '../lib/api-mutations.js';

interface VerifiedProperty {
  siteUrl: string;
  permissionLevel: 'siteOwner' | 'siteFullUser' | 'siteRestrictedUser' | 'siteUnverifiedUser';
}

interface AvailablePropertiesResponse {
  data: VerifiedProperty[];
}

export interface SearchConsolePropertyPickerProps {
  brandId: string;
  onComplete: () => void;
  onCancel: () => void;
}

export function SearchConsolePropertyPicker({
  brandId,
  onComplete,
  onCancel,
}: SearchConsolePropertyPickerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [properties, setProperties] = useState<VerifiedProperty[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(
          `/api/v1/brands/${brandId}/search-console/available-properties`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            (body as { error?: string }).error ?? `HTTP ${res.status}`,
          );
        }
        const body = (await res.json()) as AvailablePropertiesResponse;
        if (cancelled) return;
        setProperties(body.data);
        if (body.data.length > 0) setSelected(body.data[0]!.siteUrl);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [brandId]);

  const handleConfirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiPost(`/api/v1/brands/${brandId}/search-console/select-property`, {
        site_url: selected,
      });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="gsc-picker-loading"
        className="mt-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-500"
      >
        <Spinner  />
        Loading your verified Search Console properties…
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="gsc-picker-error"
        className="mt-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
      >
        <p className="font-medium">Could not load properties</p>
        <p className="mt-1 text-red-600">{error}</p>
        <Button variant="ghost" onClick={onCancel}>
          <Icon name="bx-x" />
          Dismiss
        </Button>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div
        data-testid="gsc-picker-empty"
        className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
      >
        <p className="font-medium">No verified Search Console properties found</p>
        <p className="mt-1 text-amber-700">
          Your Google account doesn't have any verified Search Console properties.
          Add and verify a property in Google Search Console, then return here.
        </p>
        <Button variant="ghost" onClick={onCancel}>
          Dismiss
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="gsc-picker"
      className="mt-3 rounded-md border border-violet-200 bg-violet-50/40 p-4"
    >
      <h3 className="text-sm font-semibold text-slate-900">
        Pick a Search Console property
      </h3>
      <p className="mt-1 text-xs text-slate-500">
        Choose which of your verified properties should bind to this brand. You
        can change it later by disconnecting and reconnecting.
      </p>
      <ul className="mt-3 space-y-2" data-testid="gsc-picker-list">
        {properties.map((p) => (
          <li key={p.siteUrl}>
            <label
              className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm ${
                selected === p.siteUrl
                  ? 'border-violet-400 bg-white '
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <input
                type="radio"
                name="gsc-property"
                value={p.siteUrl}
                checked={selected === p.siteUrl}
                onChange={() => setSelected(p.siteUrl)}
                className="text-violet-600 focus:ring-violet-500"
                data-testid={`gsc-picker-radio-${p.siteUrl}`}
              />
              <div className="flex flex-1 items-center justify-between">
                <span className="font-medium text-slate-700">{p.siteUrl}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium capitalize text-slate-500">
                  {p.permissionLevel.replace(/^site/, '')}
                </span>
              </div>
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          <Icon name="bx-x" />
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void handleConfirm()} disabled={!selected || submitting} data-testid="gsc-picker-confirm">
          {submitting ? <Spinner /> : <Icon name="bx-check" />}
          Confirm
        </Button>
      </div>
    </div>
  );
}
